import { create } from 'zustand';
import type { TestSuite, TestCase } from '@k-ucap/shared';
import { createRepositories } from '@/infrastructure/repositories/RepositoryFactory.js';

export interface TestSuiteState {
  suites: TestSuite[];
  cases: TestCase[];
  selectedSuiteId: string | null;
  selectedCaseId: string | null;
  isLoading: boolean;
  error: string | null;
  loadRequestId: number;

  loadSuites: (projectId: string) => Promise<void>;
  loadCases: (suiteId: string) => Promise<void>;
  selectSuite: (id: string | null) => Promise<void>;
  selectCase: (id: string | null) => void;
  createSuite: (projectId: string, userId: string, environment: string) => Promise<TestSuite>;
  updateSuite: (suite: TestSuite) => Promise<void>;
  duplicateSuite: (id: string) => Promise<void>;
  deleteSuite: (id: string) => Promise<void>;
  createCase: (suiteId: string, projectId: string, userId: string, environment: string) => Promise<TestCase>;
  updateCase: (testCase: TestCase) => Promise<void>;
  duplicateCase: (id: string) => Promise<void>;
  deleteCase: (id: string) => Promise<void>;
  clearError: () => void;
  reset: () => void;
}

let nextRequestId = 0;

export const useTestSuiteStore = create<TestSuiteState>((set, get) => {
  const { testSuiteRepository, testCaseRepository } = createRepositories();

  return {
    suites: [],
    cases: [],
    selectedSuiteId: null,
    selectedCaseId: null,
    isLoading: false,
    error: null,
    loadRequestId: 0,

    loadSuites: async (projectId) => {
      const requestId = ++nextRequestId;
      set({ isLoading: true, error: null, loadRequestId: requestId });
      try {
        const suites = await testSuiteRepository.findByProjectId(projectId);
        if (get().loadRequestId !== requestId) return;
        set({ suites, isLoading: false });
      } catch (err) {
        if (get().loadRequestId !== requestId) return;
        set({ error: (err as Error).message, isLoading: false });
      }
    },

    loadCases: async (suiteId) => {
      const requestId = ++nextRequestId;
      set({ isLoading: true, error: null, loadRequestId: requestId });
      try {
        const cases = await testCaseRepository.findBySuiteId(suiteId);
        if (get().loadRequestId !== requestId) return;
        if (get().selectedSuiteId !== suiteId) return;
        set({ cases, isLoading: false });
      } catch (err) {
        if (get().loadRequestId !== requestId) return;
        set({ error: (err as Error).message, isLoading: false });
      }
    },

    selectSuite: (id) => {
      const requestId = ++nextRequestId;
      set({ selectedSuiteId: id, selectedCaseId: null, cases: [], loadRequestId: requestId });
      if (id) {
        return get().loadCases(id);
      }
      return Promise.resolve();
    },

    selectCase: (id) => set({ selectedCaseId: id }),

    createSuite: async (projectId, userId, _environment) => {
      const newSuite: TestSuite = {
        id: `suite-${Date.now()}`,
        projectId,
        name: '새 시험 그룹',
        description: '',
        category: 'Flight',
        tags: [],
        version: '1.0',
        testCaseIds: [],
        createdBy: userId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      await testSuiteRepository.save(newSuite);
      set({ suites: [...get().suites, newSuite], selectedSuiteId: newSuite.id });
      return newSuite;
    },

    updateSuite: async (suite) => {
      const updated: TestSuite = { ...suite, updatedAt: new Date().toISOString() };
      await testSuiteRepository.save(updated);
      set({ suites: get().suites.map((s) => (s.id === updated.id ? updated : s)) });
    },

    duplicateSuite: async (id) => {
      const duplicated = await testSuiteRepository.duplicate(id);
      if (!duplicated) return;
      set({ suites: [...get().suites, duplicated], selectedSuiteId: duplicated.id });
    },

    deleteSuite: async (id) => {
      await testSuiteRepository.delete(id);
      set({
        suites: get().suites.filter((s) => s.id !== id),
        selectedSuiteId: get().selectedSuiteId === id ? null : get().selectedSuiteId,
      });
    },

    createCase: async (suiteId, projectId, userId, _environment) => {
      const newCase: TestCase = {
        id: `tc-${Date.now()}`,
        suiteId,
        projectId,
        name: '새 시험 케이스',
        description: '',
        objective: '',
        preconditions: [],
        messageRules: [],
        expectedResults: [],
        passCriteria: [{ type: 'NO_FAIL', params: {} }],
        timeoutMs: 60000,
        executionOrder: get().cases.length,
        enabled: true,
        tags: [],
        createdBy: userId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      await testCaseRepository.save(newCase);
      set({ cases: [...get().cases, newCase], selectedCaseId: newCase.id });
      return newCase;
    },

    updateCase: async (testCase) => {
      const updated: TestCase = { ...testCase, updatedAt: new Date().toISOString() };
      await testCaseRepository.save(updated);
      set({ cases: get().cases.map((c) => (c.id === updated.id ? updated : c)) });
    },

    duplicateCase: async (id) => {
      const duplicated = await testCaseRepository.duplicate(id);
      if (!duplicated) return;
      set({ cases: [...get().cases, duplicated], selectedCaseId: duplicated.id });
    },

    deleteCase: async (id) => {
      await testCaseRepository.delete(id);
      set({
        cases: get().cases.filter((c) => c.id !== id),
        selectedCaseId: get().selectedCaseId === id ? null : get().selectedCaseId,
      });
    },

    clearError: () => set({ error: null }),
    reset: () =>
      set({
        suites: [],
        cases: [],
        selectedSuiteId: null,
        selectedCaseId: null,
        isLoading: false,
        error: null,
        loadRequestId: 0,
      }),
  };
});

export default useTestSuiteStore;
